/**
 * Date Utilities
 * Date validation and manipulation functions
 */

export class DateUtils {
  /**
   * Parse YYYY-MM-DD string as local date
   * @param {Date|string} date - Date to parse
   * @returns {Date|null}
   */
  static parseLocalDate(date) {
    if (!date) return null;
    if (date instanceof Date) return date;
    const str = String(date).trim();
    const match = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const [, y, m, d] = match.map(Number);
      return new Date(y, m - 1, d);
    }
    return new Date(date);
  }

  /**
   * Check if date is in the future
   * @param {Date|string} date - Date to check
   * @returns {boolean}
   */
  static isFutureDate(date) {
    if (!date) return false;
    const d = this.parseLocalDate(date);
    if (!d || isNaN(d.getTime())) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    d.setHours(0, 0, 0, 0);

    return d > today;
  }

  /**
   * Check if date is in the past
   * @param {Date|string} date - Date to check
   * @returns {boolean}
   */
  static isPastDate(date) {
    if (!date) return false;

    // Safeguard: if date string equals local today (YYYY-MM-DD), it's not past
    const str = String(date).trim();
    const dateMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (dateMatch) {
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      if (dateMatch[0] === todayStr) return false;
    }

    const d = this.parseLocalDate(date);
    if (!d || isNaN(d.getTime())) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    d.setHours(0, 0, 0, 0);

    return d < today;
  }

  /**
   * Check if date is valid
   * @param {Date|string} date - Date to check
   * @returns {boolean}
   */
  static isValidDate(date) {
    if (!date) return false;
    const d = date instanceof Date ? date : new Date(date);
    return !isNaN(d.getTime());
  }

  /**
   * Check if date is within range
   * @param {Date|string} date - Date to check
   * @param {Date|string} fromDate - Start date
   * @param {Date|string} toDate - End date
   * @returns {boolean}
   */
  static isDateInRange(date, fromDate, toDate) {
    const d = this.parseLocalDate(date);
    const from = this.parseLocalDate(fromDate);
    const to = this.parseLocalDate(toDate);
    if (
      !d ||
      !from ||
      !to ||
      isNaN(d.getTime()) ||
      isNaN(from.getTime()) ||
      isNaN(to.getTime())
    ) {
      return false;
    }

    d.setHours(0, 0, 0, 0);
    from.setHours(0, 0, 0, 0);
    to.setHours(0, 0, 0, 0);

    return d >= from && d <= to;
  }

  /**
   * Calculate age from birthdate
   * @param {Date|string} birthdate - Birth date
   * @returns {number}
   */
  static calculateAge(birthdate) {
    if (!this.isValidDate(birthdate)) return 0;

    const birth = birthdate instanceof Date ? birthdate : new Date(birthdate);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birth.getDate())
    ) {
      age--;
    }

    return age;
  }

  /**
   * Get years difference between two dates
   * @param {Date|string} date1 - First date
   * @param {Date|string} date2 - Second date
   * @returns {number}
   */
  static getYearsDifference(date1, date2) {
    if (!this.isValidDate(date1) || !this.isValidDate(date2)) return 0;

    const d1 = date1 instanceof Date ? date1 : new Date(date1);
    const d2 = date2 instanceof Date ? date2 : new Date(date2);

    return Math.abs(d1.getFullYear() - d2.getFullYear());
  }

  /**
   * Get year from date
   * @param {Date|string} date - Date
   * @returns {number}
   */
  static getYear(date) {
    if (!this.isValidDate(date)) return 0;
    const d = date instanceof Date ? date : new Date(date);
    return d.getFullYear();
  }

  /**
   * Format date to YYYY-MM-DD
   * @param {Date|string} date - Date to format
   * @returns {string}
   */
  static formatDate(date) {
    if (!this.isValidDate(date)) return "";

    const d = date instanceof Date ? date : new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  }

  /**
   * Get today's date as YYYY-MM-DD
   * @returns {string}
   */
  static getToday() {
    return this.formatDate(new Date());
  }

  /**
   * Normalize a date value to YYYY-MM-DD for server submission.
   * If already YYYY-MM-DD, returns as-is. Otherwise parses and formats; returns '' if invalid.
   * @param {string|Date} value - Date value from form (may be YYYY-MM-DD or other format)
   * @returns {string} YYYY-MM-DD or ''
   */
  static toYYYYMMDD(value) {
    if (value == null || String(value).trim() === '') return '';
    const str = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
    const d = this.parseLocalDate(str);
    if (!d || isNaN(d.getTime())) return '';
    return this.formatDate(d);
  }

  /**
   * Local calendar date (YYYY-MM-DD) for display — from ISO datetime, unix, or date-only string.
   * @param {string|number} value
   * @returns {string}
   */
  static toDisplayDate(value) {
    if (value == null || String(value).trim() === '') return '';
    const str = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
    const ts = parseInt(str, 10);
    if (!isNaN(ts) && /^\d+$/.test(str)) {
      return this.formatDate(new Date(ts * 1000));
    }
    const d = new Date(str);
    if (!d || isNaN(d.getTime())) return '';
    return this.formatDate(d);
  }
}